// ============================================================================
// CMS Animation Wrapper (M11/Phase2) — Entrance + continuous animations
// Supports mobile_disabled, stagger context, continuous pulse/float.
// ============================================================================

import { useRef, useState, useEffect, useCallback, type ReactNode } from 'react';
import { Box } from '@chakra-ui/react';
import type { AnimationConfig } from '../editor/types';
import { usePreviewDevice } from './PreviewDeviceContext';
import { useStaggerContext } from './contexts/StaggerContext';

const KEYFRAME_MAP: Record<string, string> = {
  'fade-in': 'cms-fade-in',
  'slide-up': 'cms-slide-up',
  'slide-down': 'cms-slide-down',
  'slide-left': 'cms-slide-left',
  'slide-right': 'cms-slide-right',
  'scale-in': 'cms-scale-in',
};

const CONTINUOUS_MAP: Record<string, string> = {
  pulse: 'cms-pulse 2s ease-in-out infinite',
  float: 'cms-float 3s ease-in-out infinite',
};

interface AnimationWrapperProps {
  animation: AnimationConfig | undefined;
  children: ReactNode;
}

export const AnimationWrapper = ({ animation, children }: AnimationWrapperProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [triggered, setTriggered] = useState(false);
  const [entranceDone, setEntranceDone] = useState(false);
  const previewDevice = usePreviewDevice();
  const stagger = useStaggerContext();

  const isActive = !!animation && animation.entrance !== 'none';
  const hasContinuous = !!animation?.continuous && animation.continuous !== 'none';
  const mobileDisabled = animation?.mobile_disabled ?? false;
  const skipForMobilePreview = (isActive || hasContinuous) && mobileDisabled && previewDevice === 'mobile';

  // Stagger-aware delay
  const baseDelay = animation?.delay ?? 0;
  const staggerOffset = stagger ? stagger.index * stagger.delayStep : 0;
  const effectiveDelay = baseDelay + staggerOffset;

  useEffect(() => {
    if (!isActive || skipForMobilePreview) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTriggered(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [isActive, skipForMobilePreview]);

  const handleAnimationEnd = useCallback((e: React.AnimationEvent) => {
    if (e.target === ref.current) {
      setEntranceDone(true);
    }
  }, []);

  // No animation at all — render children directly
  if (!isActive && !hasContinuous) {
    return <>{children}</>;
  }

  // Mobile preview kill switch
  if (skipForMobilePreview) {
    return <>{children}</>;
  }

  // Continuous-only (no entrance)
  if (!isActive && hasContinuous) {
    const continuousValue = CONTINUOUS_MAP[animation!.continuous!];
    const mobileSx = mobileDisabled && !previewDevice
      ? {
          '&[data-mobile-disabled="true"]': {
            '@media (max-width: 768px)': { animation: 'none !important' },
          },
        }
      : undefined;

    return (
      <Box
        ref={ref}
        className="cms-continuous"
        data-mobile-disabled={mobileDisabled ? 'true' : undefined}
        sx={{ animation: continuousValue, ...mobileSx }}
      >
        {children}
      </Box>
    );
  }

  // Entrance animation (with optional continuous after)
  const keyframe = KEYFRAME_MAP[animation!.entrance];
  const duration = animation!.duration ?? 400;
  const easing = animation!.easing ?? 'ease-out';

  const entranceAnimation = triggered
    ? `${keyframe} ${duration}ms ${easing} ${effectiveDelay}ms both`
    : undefined;

  const continuousValue = hasContinuous && entranceDone
    ? CONTINUOUS_MAP[animation!.continuous!]
    : undefined;

  const mobileSx = mobileDisabled && !previewDevice
    ? {
        '&[data-mobile-disabled="true"]': {
          '@media (max-width: 768px)': {
            animation: 'none !important',
            opacity: '1 !important',
            transform: 'none !important',
          },
        },
      }
    : undefined;

  return (
    <Box
      ref={ref}
      className={`cms-animate${hasContinuous ? ' cms-continuous' : ''}`}
      data-mobile-disabled={mobileDisabled ? 'true' : undefined}
      onAnimationEnd={handleAnimationEnd}
      sx={{
        opacity: triggered ? undefined : 0,
        animation: continuousValue ?? entranceAnimation,
        ...mobileSx,
      }}
    >
      {children}
    </Box>
  );
};
